import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Phone } from 'lucide-react';
import brand from '../../config/brand';

const countries = [
  { code: 'US', name: 'United States', dialCode: '+1' },
  { code: 'CA', name: 'Canada', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
  { code: 'AU', name: 'Australia', dialCode: '+61' },
  { code: 'DE', name: 'Germany', dialCode: '+49' },
  { code: 'FR', name: 'France', dialCode: '+33' },
  { code: 'ES', name: 'Spain', dialCode: '+34' },
  { code: 'IT', name: 'Italy', dialCode: '+39' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31' },
  { code: 'SE', name: 'Sweden', dialCode: '+46' },
  { code: 'NO', name: 'Norway', dialCode: '+47' },
  { code: 'DK', name: 'Denmark', dialCode: '+45' },
  { code: 'FI', name: 'Finland', dialCode: '+358' },
  { code: 'IE', name: 'Ireland', dialCode: '+353' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64' },
  { code: 'SG', name: 'Singapore', dialCode: '+65' },
  { code: 'JP', name: 'Japan', dialCode: '+81' },
  { code: 'KR', name: 'South Korea', dialCode: '+82' },
  { code: 'IN', name: 'India', dialCode: '+91' },
  { code: 'BR', name: 'Brazil', dialCode: '+55' },
  { code: 'MX', name: 'Mexico', dialCode: '+52' },
  { code: 'CN', name: 'China', dialCode: '+86' },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41' },
  { code: 'AT', name: 'Austria', dialCode: '+43' },
  { code: 'BE', name: 'Belgium', dialCode: '+32' },
  { code: 'PT', name: 'Portugal', dialCode: '+351' },
  { code: 'PL', name: 'Poland', dialCode: '+48' },
  { code: 'RU', name: 'Russia', dialCode: '+7' },
  { code: 'IL', name: 'Israel', dialCode: '+972' },
  { code: 'PH', name: 'Philippines', dialCode: '+63' },
  { code: 'TH', name: 'Thailand', dialCode: '+66' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84' },
  { code: 'AR', name: 'Argentina', dialCode: '+54' },
  { code: 'CL', name: 'Chile', dialCode: '+56' },
  { code: 'CO', name: 'Colombia', dialCode: '+57' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234' },
  { code: 'EG', name: 'Egypt', dialCode: '+20' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880' },
  { code: 'TR', name: 'Turkey', dialCode: '+90' },
  { code: 'GR', name: 'Greece', dialCode: '+30' },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420' },
  { code: 'RO', name: 'Romania', dialCode: '+40' },
  { code: 'HU', name: 'Hungary', dialCode: '+36' },
];

export default function AuthModal({ open, onClose, initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();

  useEffect(() => {
    if (open) {
      setMode(initialMode);
    }
  }, [open, initialMode]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    country: '',
    phone: '',
    tcpa_optin: false,
    marketing_optin: false,
    terms_accepted: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const selectedCountry = countries.find(c => c.code === formData.country);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.name) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        if (!formData.country) {
          toast.error('Please select your country');
          setLoading(false);
          return;
        }
        if (!formData.terms_accepted) {
          toast.error('Please accept the Terms of Service and Privacy Policy');
          setLoading(false);
          return;
        }
        await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          country: formData.country,
          phone: formData.phone ? `${selectedCountry?.dialCode || ''}${formData.phone}` : '',
          tcpa_optin: formData.tcpa_optin,
        });
        toast.success('Account created successfully!');
      }
      onClose();
      setFormData({ 
        email: '', 
        password: '', 
        name: '', 
        country: '',
        phone: '',
        tcpa_optin: false, 
        marketing_optin: false, 
        terms_accepted: false 
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border border-[#E2E8F0] shadow-2xl max-w-md max-h-[90vh] overflow-y-auto rounded-none" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-[#0A192F]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Free Account'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {mode === 'register' && (
            <>
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Full Name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
                  placeholder="John Doe"
                  data-testid="register-name-input"
                  required={mode === 'register'}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Country <span className="text-red-600">*</span>
                </Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, country: value }))}
                >
                  <SelectTrigger 
                    className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none" 
                    data-testid="register-country-select"
                  >
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E2E8F0] shadow-lg max-h-[300px] z-[100] rounded-none">
                    {countries.map((country) => (
                      <SelectItem 
                        key={country.code} 
                        value={country.code} 
                        className="text-[#1A1A1A] hover:bg-[#F0F4F8] cursor-pointer"
                      >
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <div className="w-24 flex-shrink-0">
                    <div className="bg-[#F0F4F8] border border-[#E2E8F0] rounded-none h-10 flex items-center justify-center text-[#64748B] text-sm">
                      {selectedCountry?.dialCode || '+1'}
                    </div>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" strokeWidth={1.5} />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
                      placeholder="555 123 4567"
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Email Address <span className="text-red-600">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] rounded-none"
              placeholder="you@example.com"
              data-testid="auth-email-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-widest text-[#64748B] mb-2 block" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Password <span className="text-red-600">*</span>
            </Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                className="bg-white border-[#E2E8F0] focus:border-[#C5A059] text-[#1A1A1A] pr-10 rounded-none"
                placeholder="Enter your password"
                data-testid="auth-password-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#1A1A1A]"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div className="space-y-4 pt-4 border-t border-[#E2E8F0]">
              {/* Terms & Privacy Agreement */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms_accepted"
                  checked={formData.terms_accepted}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, terms_accepted: checked }))}
                  className="mt-1 border-[#E2E8F0] data-[state=checked]:bg-[#0A192F] data-[state=checked]:border-[#0A192F]"
                  data-testid="terms-checkbox"
                />
                <Label htmlFor="terms_accepted" className="text-xs text-[#64748B] leading-relaxed cursor-pointer" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#C5A059] hover:underline" onClick={onClose}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-[#C5A059] hover:underline" onClick={onClose}>
                    Privacy Policy
                  </Link>{' '}
                  <span className="text-red-600">*</span>
                </Label>
              </div>

              {/* Transactional Messages Consent */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="tcpa_optin"
                  checked={formData.tcpa_optin}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, tcpa_optin: checked }))}
                  className="mt-1 border-[#E2E8F0] data-[state=checked]:bg-[#0A192F] data-[state=checked]:border-[#0A192F]"
                  data-testid="tcpa-checkbox"
                />
                <Label htmlFor="tcpa_optin" className="text-xs text-[#64748B] leading-relaxed cursor-pointer" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  By checking this box, I consent to receive transactional messages from {brand.name} related to my account, such as account creation confirmations and account status notifications.
                </Label>
              </div>

              {/* Marketing Messages Consent */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="marketing_optin"
                  checked={formData.marketing_optin}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, marketing_optin: checked }))}
                  className="mt-1 border-[#E2E8F0] data-[state=checked]:bg-[#0A192F] data-[state=checked]:border-[#0A192F]"
                  data-testid="marketing-checkbox"
                />
                <Label htmlFor="marketing_optin" className="text-xs text-[#64748B] leading-relaxed cursor-pointer" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  By checking this box, I agree to receive marketing messages from {brand.name}, including promotional offers and updates.
                </Label>
              </div>

              {/* SMS Disclosure */}
              <div className="bg-[#F0F4F8] border border-[#E2E8F0] rounded-none p-3 mt-4">
                <p className="text-[10px] text-[#64748B] leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  <span className="text-[#0A192F]">*</span>Consent to receive SMS messages is optional and not a condition for using {brand.name} services. Message and data rates may apply. You may receive up to 4 messages per week. You can opt out at any time by texting STOP. For assistance, text HELP or contact us at{' '}
                  <a href={`mailto:${brand.supportEmail}`} className="text-[#C5A059] hover:underline">
                    {brand.supportEmail}
                  </a>
                  . Carriers are not responsible for delayed or undelivered messages. All information collected during the SMS opt-in process will not be sold, rented, or shared. View our{' '}
                  <Link to="/terms" className="text-[#C5A059] hover:underline" onClick={onClose}>
                    Terms of Use
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-[#C5A059] hover:underline" onClick={onClose}>
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#0A192F] text-white hover:bg-[#0A192F]/90 uppercase tracking-widest text-xs py-2 rounded-none mt-6"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : mode === 'login' ? (
              'Sign In'
            ) : (
              'Create Free Account'
            )}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-sm text-[#64748B] hover:text-[#0A192F] transition-colors"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid="auth-mode-toggle"
          >
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <span className="text-[#C5A059]">{mode === 'login' ? 'Sign Up' : 'Sign In'}</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
